import { axios, getAxiosConfig } from '@config/axios';
import Environment from '@config/index';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { NegotiationEventRequest } from '@interfaces/models/negotiation/negotiationEventRequest';
import { ApiResponse } from '@interfaces/api';
import { UserTransactionRole } from '@interfaces/models/userTransactionRole';
import { BuyingOffersResponse } from '@interfaces/models/negotiation/domain/BuyingOffer';
import {
  SellingOffersProduct,
  SellingOffersProductListResponse,
} from '@interfaces/models/negotiation/domain/SellingOffersProduct';
import { mapSellingOffersNegotiationListResponse } from '@interfaces/models/negotiation/mappers/sellingOffersNegotiationsMapper';
import { SellingOffersNegotiationDataDTO } from '@interfaces/models/negotiation/dtos/SellingOffersNegotiationDTO';
import {
  ListingsProducts,
  OfferSettingsProduct,
  OfferSettingsStatus,
  OfferSettingsSavePayload,
} from '@interfaces/models/negotiation/domain/ListingsProducts';
import { mapListingsProductsResponse } from '@interfaces/models/negotiation/mappers/listingsProductsMapper';
import {
  ListingsProductsDTO,
  OfferSettingsProductsDTO,
  OfferSettingsSaveDTO,
} from '@interfaces/models/negotiation/dtos/ListingsProductsDTO';
import { ListingsMarkItemAsShippedDTO } from '@interfaces/models/negotiation/dtos/ListingsMarkItemAsShippedDTO';
import { mapListingsItemsToShipResponse } from '@interfaces/models/negotiation/mappers/listingsItemsToShipMapper';
import { ListingsItemToShip } from '@interfaces/models/negotiation/domain/ListingsItemsToShip';
import { ListingsItemToShipDTO } from '@interfaces/models/negotiation/dtos/ListingsItemsToShipDTO';
import {
  mapSellingOffersProductFromDTO,
  mapSellingOffersProductListResponse,
} from '@interfaces/models/negotiation/mappers/sellingOffersProductsMapper';
import { mapBuyingOffersResponseFromDTO } from '@interfaces/models/negotiation/mappers/buyingOfferMapper';
import { BuyingOfferDTO } from '@interfaces/models/negotiation/dtos/BuyingOfferDTO';
import {
  SellingOffersProductDataDTO,
  SellingOffersProductDTO,
} from '@interfaces/models/negotiation/dtos/SellingOffersProductDTO';
import { SellingOffersNegotiationListResponse } from '@interfaces/models/negotiation/domain/SellingOffersNegotiation';
import {
  mapOfferSettingsListResponse,
  mapOfferSettingsSaveResponse,
} from '@interfaces/models/negotiation/mappers/listingsOfferSettingsMapper';
import { isNumberId } from '@helpers/utils/general';
import { NegotiationDTO } from '@interfaces/models/negotiation/dtos/NegotiationDTO';

export class NegotiationsService {
  static apiBaseUrl = Environment.apiBaseUrl;

  static async listNegotiations(role: UserTransactionRole, withEvents: boolean): Promise<Negotiation[]> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.withEvents = withEvents ?? null;

    const {
      data: { data },
    } = await axios.get<ApiResponse<NegotiationDTO[]>>(`${this.apiBaseUrl}/negotiations`, axiosConfig);
    return data;
  }

  static async listBuyingOffersNegotiations(limit: number, offset: number): Promise<BuyingOffersResponse> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<ApiResponse<BuyingOfferDTO[]>>(
      `${this.apiBaseUrl}/users/me/negotiation-buying-offers`,
      axiosConfig,
    );

    return mapBuyingOffersResponseFromDTO(response.data);
  }

  static async getSellingOffersProductById(
    role: UserTransactionRole,
    limit: number,
    offset: number,
    id: number,
  ): Promise<SellingOffersProduct> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<ApiResponse<SellingOffersProductDTO>>(
      `${this.apiBaseUrl}/users/me/selling-offers/${id}/product`,
      axiosConfig,
    );

    return mapSellingOffersProductFromDTO(response.data.data);
  }

  static async listSellingOffersProducts(
    role: UserTransactionRole,
    limit: number,
    offset: number,
  ): Promise<SellingOffersProductListResponse> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<ApiResponse<SellingOffersProductDataDTO[]>>(
      `${this.apiBaseUrl}/users/me/selling-offers`,
      axiosConfig,
    );

    return mapSellingOffersProductListResponse(response.data);
  }

  static async listSellingOffersNegotiations(
    id: number,
    role: UserTransactionRole,
    limit: number,
    offset: number,
  ): Promise<SellingOffersNegotiationListResponse> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<ApiResponse<SellingOffersNegotiationDataDTO[]>>(
      `${this.apiBaseUrl}/users/me/selling-offers/${id}/negotiations`,
      axiosConfig,
    );

    return mapSellingOffersNegotiationListResponse(response.data);
  }

  static async listListingsProducts(limit: number, offset: number, sortBy: string): Promise<ListingsProducts> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;
    axiosConfig.params.sort = sortBy;

    const response = await axios.get<ListingsProductsDTO>(`${this.apiBaseUrl}/users/me/product-listings`, axiosConfig);
    return mapListingsProductsResponse(response.data);
  }

  static async listListingsItemsToShip(limit: number, offset: number): Promise<ListingsItemToShip[]> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<ApiResponse<ListingsItemToShipDTO[]>>(
      `${this.apiBaseUrl}/users/me/awaiting-shipping-items`,
      axiosConfig,
    );

    return mapListingsItemsToShipResponse(response.data);
  }

  static async markItemAsShipped(id: number): Promise<string> {
    const axiosConfig = getAxiosConfig();

    const response = await axios.post<ListingsMarkItemAsShippedDTO>(
      `${this.apiBaseUrl}/products/${id}/confirm-sendin`,
      {},
      axiosConfig,
    );

    return response.data.data;
  }

  static async offerSettingsProducts(): Promise<OfferSettingsProduct[]> {
    const axiosConfig = getAxiosConfig();
    const response = await axios.get<OfferSettingsProductsDTO>(`${this.apiBaseUrl}/users/me/mmao-opt-in`, axiosConfig);
    return mapOfferSettingsListResponse(response.data);
  }

  static async saveOfferSettings(payload: OfferSettingsSavePayload): Promise<OfferSettingsStatus> {
    const axiosConfig = getAxiosConfig();
    const response = await axios.patch<OfferSettingsSaveDTO>(
      `${this.apiBaseUrl}/users/me/mmao-opt-in`,
      payload,
      axiosConfig,
    );
    return mapOfferSettingsSaveResponse(response.data);
  }

  static async getNegotiation(id: Negotiation['id'], withEvents: boolean): Promise<Negotiation | undefined> {
    if (!isNumberId(id)) {
      throw new Error(`Invalid negotiation ID: ${id}`);
    }

    const axiosConfig = getAxiosConfig();
    if (withEvents) {
      axiosConfig.params.withEvents = withEvents;
    }

    const {
      data: { data },
    } = await axios.get<ApiResponse<NegotiationDTO | undefined>>(`${this.apiBaseUrl}/negotiations/${id}`, axiosConfig);

    return data || undefined;
  }

  static async addNegotiationEvent(
    id: Negotiation['id'],
    payload: NegotiationEventRequest,
  ): Promise<ApiResponse<Negotiation>> {
    const axiosConfig = getAxiosConfig();

    const { data } = await axios.post<ApiResponse<NegotiationDTO>>(
      `${this.apiBaseUrl}/negotiations/${encodeURIComponent(String(id))}/events`,
      payload,
      axiosConfig,
    );
    return data;
  }

  static async executeFPD(
    id: Negotiation['id'],
    payload: NegotiationEventRequest | {},
  ): Promise<ApiResponse<Negotiation>> {
    const axiosConfig = getAxiosConfig();

    const { data } = await axios.post<ApiResponse<NegotiationDTO>>(
      `${this.apiBaseUrl}/negotiations/${encodeURIComponent(String(id))}/flash-price-drop`,
      payload,
      axiosConfig,
    );
    return data;
  }
}
